import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', to: '/', external: false },
  { label: 'About Us', to: '/about', external: false },
  { label: 'Our Services', to: '/services', external: false },
  { label: 'Rentals', to: 'https://rentals.galaxyhousing.com.au', external: true },
  { label: 'Contact Us', to: '/contact', external: false },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isTransparent = !scrolled && !menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-navy-900/95 backdrop-blur-md shadow-lg'
      }`}
    >
      {/* Top contact bar */}
      <div className={`transition-all duration-300 ${isTransparent ? 'bg-navy-900/60' : 'bg-navy-950'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-end gap-6 py-2">
          <a href="tel:0483944617" className="flex items-center gap-2 text-gold-400 text-sm hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-xs" />
            </div>
            0483 944 617
          </a>
          <a href="mailto:rentals@galaxyhousing.com.au" className="flex items-center gap-2 text-gold-400 text-sm hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-fill text-xs" />
            </div>
            rentals@galaxyhousing.com.au
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-10 h-10 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/18ca25ea8a69d5eb1e849ccadbb7687f/672c165fcb7ce82117b4150406656ead.jpeg"
              alt="Galaxy Housing"
              className="w-10 h-10 rounded-full object-cover"
            />
          </div>
          <div>
            <span className="font-heading font-bold text-white text-lg leading-tight block">Galaxy Housing</span>
            <span className="text-gold-400 text-xs tracking-widest uppercase">Feel At Home, Wherever You Are</span>
          </div>
        </Link>

        {/* Desktop nav links */}
        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            const active = !link.external && location.pathname === link.to;
            return (
              <li key={link.to}>
                {link.external ? (
                  <a
                    href={link.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 text-sm font-medium tracking-wide transition-all duration-200 rounded cursor-pointer whitespace-nowrap text-white/80 hover:text-white hover:bg-white/10"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.to}
                    className={`px-4 py-2 text-sm font-medium tracking-wide transition-all duration-200 rounded cursor-pointer whitespace-nowrap ${
                      active
                        ? 'text-gold-400 border-b-2 border-gold-400'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        {/* CTA button */}
        <a
          href="https://rentals.galaxyhousing.com.au"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold text-sm px-5 py-2.5 rounded transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-home-heart-line text-sm" />
          </div>
          View Rentals
        </a>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden text-white p-2 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </div>
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-navy-900 border-t border-white/10 px-6 py-4 flex flex-col gap-2">
          {navLinks.map((link) => {
            const active = !link.external && location.pathname === link.to;
            return link.external ? (
              <a
                key={link.to}
                href={link.to}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 px-4 rounded text-sm font-medium transition-colors cursor-pointer text-white/80 hover:bg-white/10 hover:text-white"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`py-3 px-4 rounded text-sm font-medium transition-colors cursor-pointer ${
                  active ? 'bg-gold-500/20 text-gold-400' : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <a
            href="https://rentals.galaxyhousing.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold text-sm py-3 rounded cursor-pointer whitespace-nowrap"
          >
            View Available Rentals
          </a>
        </div>
      )}
    </header>
  );
}
